import { getStorage, ref, uploadBytes, getDownloadURL, listAll } from "firebase/storage";
import { app } from "./index";

/**
 * Uploads a file to Firebase Storage and returns the download URL.
 * @param file - The file to upload.
 * @param path - The path in the storage bucket where the file will be stored.
 * @returns The download URL of the uploaded file.
 */
export const uploadFileToFirebase = async (
  file: File,
  refPath: string
): Promise<string> => {
  try {
    const storage = getStorage(app);
    const storageRef = ref(storage, `${refPath}/${file.name}`);

    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    // Rethrow to let callers handle user-facing errors
    throw error;
  }
};

/**
 * Lists files under a Firebase Storage folder and returns their download URLs.
 * @param refPath - The folder path in the storage bucket to list.
 */
export const listFilesInFirebase = async (
  refPath: string
): Promise<Array<{ name: string; fullPath: string; url: string }>> => {
  const storage = getStorage(app);
  const folderRef = ref(storage, refPath);
  const result = await listAll(folderRef);
  const items = await Promise.all(
    result.items.map(async (itemRef) => {
      const url = await getDownloadURL(itemRef);
      return { name: itemRef.name, fullPath: itemRef.fullPath, url };
    })
  );
  return items;
};
